import type { RepeatableColumn, WorkflowField } from '@/api/workflows';

/** An i18n key under `formRenderer.validation.number` plus the bounds it may name. */
export type NumberRangeViolation = {
  errorKey: string;
  params: { min?: number; max?: number };
};

/**
 * Whether a figure breaks an authored minimum / maximum — and which message says so.
 *
 * Deliberately a CHECK rather than a bound on the input: handing AntD's `InputNumber` a
 * `min`/`max` makes it rewrite the advisor's figure to the bound when they leave the cell,
 * which on a dollar amount is a silent change to their money. An out-of-range figure is
 * named beneath the cell and holds Next instead, like every other column rule.
 *
 * The input is still bounded in WIDTH — see `integerDigitCap` in `numericInput.ts`, which
 * sizes the box from the maximum's digit count. The two are different rules on purpose: a
 * maximum of 123 sizes a three-digit box, so 800 is typed and then named here.
 *
 * Both bounds carry through in `params` whichever one failed, so an originator's own wording
 * can name either ("Each fund must be between {{min}} and {{max}}.").
 *
 * Blank is never a range failure — `required` owns an empty cell.
 */
export function numberRangeViolation(
  value: number,
  min: number | null | undefined,
  max: number | null | undefined,
): NumberRangeViolation | null {
  if (!Number.isFinite(value)) return null;
  const lo = min ?? null;
  const hi = max ?? null;
  const below = lo !== null && value < lo;
  const above = hi !== null && value > hi;
  if (!below && !above) return null;
  const params = {
    ...(lo !== null ? { min: lo } : {}),
    ...(hi !== null ? { max: hi } : {}),
  };
  return {
    errorKey: below ? 'formRenderer.validation.number.min' : 'formRenderer.validation.number.max',
    params,
  };
}

/**
 * {@link numberRangeViolation} for one cell of a RepeatableRow column — Number columns only.
 *
 * A non-numeric cell passes: the control only ever commits a number or null, so anything else
 * is a stored value from before the column was a Number and is not the range rule's to report.
 */
export function columnNumberRangeViolation(
  col: Pick<RepeatableColumn, 'type' | 'min' | 'max'>,
  value: unknown,
): NumberRangeViolation | null {
  if (col.type !== 'Number') return null;
  if (typeof value !== 'number') return null;
  return numberRangeViolation(value, col.min, col.max);
}

/**
 * {@link numberRangeViolation} for a scalar Number field — the same rule its columns get.
 *
 * Shared by the blur message and FormShell's Next gate so the red text and the held button can
 * never disagree, the way every other field rule here is shared. A read-only field is exempt:
 * its figure arrives from prefill or an authored default, and the advisor cannot fix it.
 */
export function fieldNumberRangeViolation(
  field: Pick<WorkflowField, 'type' | 'min' | 'max' | 'readOnly'>,
  value: unknown,
): NumberRangeViolation | null {
  if (field.type !== 'Number' || field.readOnly) return null;
  if (typeof value !== 'number') return null;
  return numberRangeViolation(value, field.min, field.max);
}
